import {
  getMembers,
  MembersListApiError,
  type MemberListItemContract,
  type MemberListSort,
  type MembersListResponse,
} from '@urbanik/api-client';
import {
  ActionList,
  ActionListItem,
  Avatar,
  Button,
  CanonicalDonutChart,
  CanonicalFilterBar,
  CanonicalPageHeader,
  ChartCard,
  ContactCell,
  DataTable,
  EmptyState,
  FilterActions,
  IconButton,
  InfoPanel,
  PageActions,
  PageSizeSelector,
  Pagination,
  PersonCell,
  QuickActionCard,
  ResultsCounter,
  SearchField,
  SelectField,
  StatePanel,
  StatusBadge,
  SummaryList,
  SummaryListItem,
  canonicalStates,
  formatDateTime,
  type DataColumn,
  type SortDirection,
} from '@urbanik/ui';
import { Eye, FunnelX, Info, Phone, Plus, Search, UserPlus, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
import { useOrganization } from './OrganizationContext';
import './members-list.css';

type State = 'loading' | 'empty' | 'error' | 'forbidden' | 'success';
const stateDef = (kind: Exclude<State, 'success'>) => canonicalStates.find((s) => s.kind === kind)!;
const initials = (member: MemberListItemContract) =>
  `${member.givenNames[0] ?? ''}${member.familyNames[0] ?? ''}`.toUpperCase();

export function MembersListView({
  canCreate = false,
  canReadDetail = false,
}: {
  canCreate?: boolean;
  canReadDetail?: boolean;
}) {
  const organization = useOrganization();
  const [scope, setScope] = useState<'active' | 'all'>('active');
  const forced = new URLSearchParams(window.location.hash.split('?')[1]).get(
    'state',
  ) as State | null;
  const initialQuery = new URLSearchParams(window.location.hash.split('?')[1]).get('query') ?? '';
  const [query, setQuery] = useState(initialQuery);
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('active');
  const [sort, setSort] = useState<MemberListSort>('name');
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [data, setData] = useState<MembersListResponse | null>(null);
  const [state, setState] = useState<State>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(query);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    if (forced && forced !== 'success') {
      setState(forced);
      return;
    }
    const controller = new AbortController();
    setState('loading');
    getMembers(
      apiBase,
      {
        scope,
        ...(debounced ? { query: debounced } : {}),
        ...(status === 'active' || status === 'inactive' ? { status } : {}),
        sort,
        direction,
        page,
        pageSize,
      },
      controller.signal,
    )
      .then((result) => {
        setData(result);
        setState(result.items.length ? 'success' : 'empty');
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error instanceof MembersListApiError && error.kind === 'unauthorized') {
          window.location.hash = '#login';
          return;
        }
        setState(
          error instanceof MembersListApiError && error.kind === 'forbidden'
            ? 'forbidden'
            : 'error',
        );
      });
    return () => controller.abort();
  }, [attempt, debounced, direction, forced, page, pageSize, scope, sort, status]);

  const columns: readonly DataColumn<MemberListItemContract>[] = [
    ...(scope === 'all'
      ? [
          {
            id: 'organization',
            header: 'Entidad',
            accessor: (r: MemberListItemContract) =>
              r.organizationName ?? 'Entidad no identificada',
            cell: (r: MemberListItemContract) => (
              <StatusBadge tone="info">{r.organizationName ?? 'Entidad'}</StatusBadge>
            ),
          },
        ]
      : []),
    {
      id: 'slotNumber',
      header: 'N.º de socio',
      accessor: (r) =>
        r.slotNumber ?? (r.hasNumberHistory ? 'Sin asignación vigente' : 'Sin número registrado'),
      sortable: true,
    },
    {
      id: 'name',
      header: 'Socio',
      accessor: (r) => `${r.familyNames} ${r.givenNames}`,
      sortable: true,
      cell: (r) => (
        <PersonCell
          avatar={<Avatar initials={initials(r)} size="small" />}
          name={`${r.familyNames} ${r.givenNames}`}
          secondary={
            r.slotNumber ??
            (r.hasNumberHistory ? 'Sin asignación vigente' : 'Sin número registrado')
          }
        />
      ),
    },
    { id: 'nationalId', header: 'Cédula', accessor: (r) => r.nationalId },
    {
      id: 'contact',
      header: 'Contacto',
      accessor: (r) => r.primaryPhone ?? r.email ?? 'No registrado',
      cell: (r) => (
        <ContactCell
          icon={<Phone aria-hidden="true" />}
          value={r.primaryPhone ?? r.email ?? 'No registrado'}
        />
      ),
    },
    {
      id: 'status',
      header: 'Estado',
      accessor: (r) => (r.status === 'active' ? 'Activo' : 'Inactivo'),
      cell: (r) => (
        <StatusBadge tone={r.status === 'active' ? 'success' : 'neutral'}>
          {r.status === 'active' ? 'Activo' : 'Inactivo'}
        </StatusBadge>
      ),
      sortable: true,
    },
    ...(canReadDetail
      ? [
          {
            id: 'detail',
            header: 'Acción',
            accessor: () => 'Ver detalle',
            cell: (r: MemberListItemContract) => (
              <IconButton
                label={`Ver detalle de ${r.familyNames} ${r.givenNames}`}
                onClick={() => {
                  window.location.hash = `#socios/${r.id}`;
                }}
              >
                <Eye aria-hidden="true" />
              </IconButton>
            ),
          },
        ]
      : []),
    {
      id: 'inactivityReason',
      header: 'Motivo de inactividad',
      accessor: (r) =>
        r.status === 'inactive' ? (r.inactivityReason ?? 'Pendiente de documentar') : '—',
    },
    {
      id: 'updatedAt',
      header: 'Actualización',
      accessor: (r) => formatDateTime(r.updatedAt),
      sortable: true,
    },
  ];
  const changeSort = (columnId: string) => {
    if (!['slotNumber', 'name', 'status', 'updatedAt'].includes(columnId)) return;
    const next = columnId as MemberListSort;
    if (sort === next) setDirection((v) => (v === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(next);
      setDirection('asc');
    }
    setPage(1);
  };
  const clear = () => {
    setQuery('');
    setStatus('active');
    setScope('active');
    setSort('name');
    setDirection('asc');
    setPage(1);
  };
  const focusSearch = () =>
    document.querySelector<HTMLInputElement>('.members-list-page .ui-table-search input')?.focus();
  const visibleActive = data?.items.filter((item) => item.status === 'active').length ?? 0;
  const visibleInactive = data?.items.filter((item) => item.status === 'inactive').length ?? 0;

  return (
    <div className="shell-page members-list-page">
      <CanonicalPageHeader
        title="Listado de socios"
        description="Consulta, filtra y administra el padrón de socios, sus datos de contacto y su estado dentro de URBANIK."
        actions={
          <PageActions>
            {canCreate ? (
              <Button
                variant="primary"
                icon={<Plus aria-hidden="true" />}
                onClick={() => {
                  window.location.hash = '#socios/nuevo';
                }}
              >
                Nuevo socio
              </Button>
            ) : null}
            <StatusBadge tone="info">
              Resultados · {data?.totalItems ?? 0}{' '}
              {status === 'active' ? 'activos' : status === 'inactive' ? 'inactivos' : 'socios'}
            </StatusBadge>
          </PageActions>
        }
      />
      <CanonicalFilterBar>
        <SelectField
          label="Alcance institucional"
          value={scope}
          onValueChange={(value) => {
            setScope(value as 'active' | 'all');
            setPage(1);
          }}
          options={[
            { value: 'active', label: organization?.active.name ?? 'Entidad activa' },
            { value: 'all', label: 'Todas las entidades autorizadas' },
          ]}
        />{' '}
        <SearchField value={query} onChange={setQuery} />
        <SelectField
          label="Estado del socio"
          value={status}
          onValueChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
          options={[
            { value: '', label: 'Todos · padrón histórico' },
            { value: 'active', label: 'Activos' },
            { value: 'inactive', label: 'Inactivos' },
          ]}
        />
        <FilterActions>
          <Button icon={<FunnelX aria-hidden="true" />} onClick={clear}>
            Limpiar filtros
          </Button>
        </FilterActions>
      </CanonicalFilterBar>
      {state !== 'success' ? (
        <StatePanel state={stateDef(state)} onAction={() => setAttempt((v) => v + 1)} />
      ) : data ? (
        <div className="members-list-layout">
          <section className="members-list-card" aria-label="Lista de socios">
            <DataTable
              paginate={false}
              rows={data.items}
              columns={columns}
              visibleColumnIds={new Set(columns.map((c) => c.id))}
              sort={
                { columnId: sort, direction: direction === 'asc' ? 'ascending' : 'descending' } as {
                  columnId: string;
                  direction: SortDirection;
                }
              }
              onSort={changeSort}
              empty={
                <EmptyState
                  title="Sin socios para mostrar"
                  description="Ajusta los filtros o registra un nuevo socio."
                />
              }
            />
            <footer>
              <ResultsCounter
                from={data.items.length ? (data.page - 1) * data.pageSize + 1 : 0}
                to={(data.page - 1) * data.pageSize + data.items.length}
                total={data.totalItems}
                noun="socios"
              />
              <Pagination
                page={data.page}
                pages={Math.max(1, data.totalPages)}
                onChange={setPage}
              />
              <PageSizeSelector
                value={pageSize}
                options={[10, 15, 25, 50]}
                onChange={(value) => {
                  setPageSize(value);
                  setPage(1);
                }}
              />
            </footer>
          </section>
          <aside className="members-list-rail" aria-label="Resumen de socios">
            <SummaryList title="Resumen de socios">
              <SummaryListItem label="Padrón histórico total" value={data.summary?.total ?? '—'} />
              <SummaryListItem
                label="Socios activos"
                value={data.summary?.active ?? '—'}
                tone="success"
              />
              <SummaryListItem
                label="Socios inactivos"
                value={data.summary?.inactive ?? '—'}
                tone="warning"
              />
              <SummaryListItem
                label="Inactivos sin causa clasificada"
                value={data.summary?.unclassifiedInactive ?? '—'}
                tone="warning"
              />
              <SummaryListItem label="Resultados del filtro" value={data.totalItems} />
              <SummaryListItem label="En esta página" value={data.items.length} />
              <SummaryListItem label="Activos visibles" value={visibleActive} tone="success" />
              <SummaryListItem label="Inactivos visibles" value={visibleInactive} tone="warning" />
            </SummaryList>
            <ChartCard title="Estado de socios" subtitle="Distribución de la página actual">
              <div className="members-list-chart">
                <CanonicalDonutChart
                  ariaLabel="Distribución de socios visibles por estado"
                  data={[
                    { label: 'Activos', value: visibleActive, color: '#0EA5B4' },
                    { label: 'Inactivos', value: visibleInactive, color: '#87AF0B' },
                  ]}
                />
              </div>
            </ChartCard>
            <section className="members-list-quick" aria-labelledby="members-quick-title">
              <h2 id="members-quick-title">Acciones rápidas</h2>
              <div>
                {canCreate ? (
                  <QuickActionCard
                    icon={<UserPlus aria-hidden="true" />}
                    title="Registrar socio"
                    description="Crear un nuevo registro"
                    onClick={() => {
                      window.location.hash = '#socios/nuevo';
                    }}
                  />
                ) : null}
                <QuickActionCard
                  icon={<Search aria-hidden="true" />}
                  title="Buscar socio"
                  description="Ir al campo de búsqueda"
                  onClick={focusSearch}
                />
              </div>
            </section>
            <InfoPanel title="Información" icon={<Info aria-hidden="true" />} tone="info">
              Mantén los datos de los socios actualizados para facilitar su gestión y comunicación.
            </InfoPanel>
            <ActionList label="Accesos del módulo">
              <ActionListItem icon={<Users aria-hidden="true" />} href="#socios">
                Listado de socios
              </ActionListItem>
            </ActionList>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
